import React, { useRef, useState } from 'react'
import Catalog from 'blocks.app/catalog/catalog'
import PreviewWebApp from 'blocks.app/preview/__webApp/preview__webApp'
import Button from 'blocks.simple/button/button'
import Modal from 'blocks.simple/modal/modal'
import filesHelpers from 'core/helpers/_files'
import { editor } from '../editor.local.methods'
import { mediaJS, transitions } from 'blocks.app/config'
import translate from 'core/translate'
import { cn } from 'ethcss'
import styles from './editor__files.jcss'
import item from 'blocks.simple/item/item.jcss'
import { api } from 'core/api/ConnectionManager'
import helpers from 'core/helpers'

interface IEditorFilesProps {
    query: string
    isMobile: boolean
    activateEditLink: (id: number) => void
    activateEditTable: (id: number) => void
    activateEditText: (id: number) => void
    minimized: boolean
    isAddLink: boolean
    isAddTable: boolean
    isAddText: boolean
    onCancelMultipleFilesSelection: () => void
    onMultipleFilesSelection: (selectedItems: { id: number; type: string; fileType: string }[]) => void
    location?: { pathname: string }
}

interface IState {
    isWebAppEdit: boolean
    selectedContent: { id: number; type: string; fileType: string } | null
    disposition: string | null
    selectedInfo: any | null
    selectedItems: { id: number; type: string; fileType: string }[]
    disableDragUploads: boolean
    editWebApp: boolean
}

const EditorFiles = (p_: IEditorFilesProps) => {
    const [state, setState] = useState<IState>({
        isWebAppEdit: false,
        selectedContent: null,
        disposition: null,
        selectedInfo: null,
        selectedItems: [],
        disableDragUploads: false,
        editWebApp: false,
    })
    let dragTimer: ReturnType<typeof setTimeout>
    const previewWebApp = useRef<any>(null)

    const closeContent = () => {
        setState((prevState) => {
            return {
                ...prevState,
                selectedItems: [],
            }
        })

        if (p_.isMobile) {
            // @ts-ignore
            editor.showToolbar(false)
        }
    }

    const showAddContentButtons = () => {
        if (p_.isMobile) {
            return true
        }
        return !!(state.selectedItems && state.selectedItems.length)
    }

    const setDisposition = (value: string) => {
        setState((prevState) => {
            return {
                ...prevState,
                disposition: value === 'list' ? 'tile' : 'list',
            }
        })
    }
    const onSelectInfo = (selectedInfo: { id: number; isDirectory: number }) => {
        setState((prevState) => {
            return {
                ...prevState,
                selectedInfo,
            }
        })

        if (!selectedInfo) return

        const data: {
            sourceId: number
            nestedQ: boolean
            sourceFolderId?: number
        } = {
            sourceId: selectedInfo.id,
            nestedQ: false,
        }

        const isDirectory = selectedInfo.isDirectory

        if (isDirectory) {
            delete data.sourceId
            data.sourceFolderId = selectedInfo.id
        }

        api.send('getAvailableFoldersFiles', data, { hideLoader: true }).then((availableFolders: any) => {
            const folders = helpers.addParentKeyIdForTree(availableFolders, 'folderId')
        })
    }

    const editContent = (file: { id: number; type: string; fileType: string }) => {
        const { activateEditLink, activateEditTable, activateEditText } = p_
        const nextState: {
            isWebAppEdit?: boolean
            selectedContent: { id: number; type: string; fileType: string }
        } = {
            selectedContent: file,
        }

        if (!helpers.isAvailable({ key: 'files', action: 'update' })) return

        if (file.fileType === 'web_app') {
            setState((prevState) => {
                return {
                    ...prevState,
                    editWebApp: true,
                }
            })

            nextState.isWebAppEdit = true

            return
        }

        const methods: {
            [index: string]: (id: number) => void
        } = {
            table: activateEditTable,
            link: activateEditLink,
            text: activateEditText,
        }

        const method = methods[file.type]

        if (method) {
            method(file.id)
        }

        setState((prevState) => {
            return {
                ...prevState,
                ...nextState,
            }
        })
    }

    const onCloseEditingWidget = () => {
        setState((prevState) => {
            return {
                ...prevState,
                isWebAppEdit: false,
                selectedContent: null,
                editWebApp: false,
            }
        })
    }

    return (
        <React.Fragment>
            <Catalog
                type="files"
                searchbarMod={'withShadow'}
                selectedInfo={state.selectedInfo}
                onSelectInfo={onSelectInfo}
                createFolder={true}
                widgetsGallery={true}
                classNameContent={cn({ [item.none]: p_.minimized })}
                useWindow={false}
                cols={4}
                onDoubleClickObject={editContent}
                showHeaderButton={true}
                disposition={state.disposition}
                changeDisposition={setDisposition}
                filterItems={[
                    { name: translate('all') },
                    { name: translate('images'), fileType: 'image', type: 'fs' },
                    { name: translate('video'), fileType: 'video', type: 'fs' },
                    { name: translate('music'), fileType: 'audio', type: 'fs' },
                    { name: translate('office'), fileType: 'office', type: 'fs' },
                    { name: translate('links'), type: 'link', fileType: 'web_page', iconName: 'link' },
                    { name: translate('streamMedia'), type: 'link', fileType: 'stream', iconName: 'stream' },
                    { name: translate('text'), type: 'text' },
                    { name: translate('tables'), type: 'table' },
                    { name: translate('web_app'), fileType: 'web_app', type: 'fs' },
                    { name: translate('input'), type: 'input' },
                ]}
                hideHeader={p_.minimized}
                isHideList={filesHelpers.isShowEditor(
                    {
                        isAddLink: p_.isAddLink,
                        isAddTable: p_.isAddTable,
                        isAddText: p_.isAddText,
                    },
                    p_.query
                )}
                toggleControls={filesHelpers.isShowEditor(p_, p_.query)}
                onChange={(list: any, type: string) => {
                    if (type === 'update') {
                        // @ts-ignore
                        editor.updateSources(list)
                    }
                }}
                onDragStart={() => {
                    if (mediaJS.tabMini.matches) {
                        clearTimeout(dragTimer)
                        dragTimer = setTimeout(() => {
                            // @ts-ignore
                            editor.toggleMinimized()
                        }, transitions.normal)
                    }
                }}
                onDragEnd={() => {
                    if (mediaJS.tabMini.matches) {
                        clearTimeout(dragTimer)
                    }
                }}
                isMobileDragEnabled
                small
                viewLocal
                isMobile={p_.isMobile}
                onMultipleFilesSelection={p_.onMultipleFilesSelection}
                onCancelMultipleFilesSelection={p_.onCancelMultipleFilesSelection}
                selectedItems={state.selectedItems}
                onSelect={(selectedItems: { id: number; type: string; fileType: string }[]) => {
                    setState((prevState) => {
                        return {
                            ...prevState,
                            selectedItems,
                        }
                    })
                }}
            />
            {showAddContentButtons() && (
                <div className={styles.multiSelectionPanel}>
                    <Button
                        mod={'fill'}
                        className={styles.contentBtn}
                        onClick={() => {
                            closeContent()
                        }}
                    >
                        {translate('cancel')}
                    </Button>
                    <Button
                        mod={'withBorder'}
                        className={cn(styles.contentBtn, 'test-add-content')}
                        disabled={!state.selectedItems.length}
                        onClick={() => {
                            p_.onMultipleFilesSelection(state.selectedItems)
                            closeContent()
                        }}
                    >
                        {translate('add')}
                    </Button>
                </div>
            )}
            {p_.location &&
            (p_.location.pathname === '/broadcasts/edit' ||
                p_.location.pathname === '/addContentToDevice' ||
                p_.location.pathname === '/broadcasts/addAdvanced') ? (
                <Modal open={state.editWebApp} onClose={onCloseEditingWidget}>
                    <PreviewWebApp
                        ref={previewWebApp}
                        item={state.selectedInfo}
                        isEditable={true}
                        indents={'small'}
                        onClose={onCloseEditingWidget}
                    />

                    <Button
                        className={styles.button}
                        onClick={() => {
                            if (previewWebApp.current && previewWebApp.current.save) {
                                previewWebApp.current.save(() => {
                                    onCloseEditingWidget()
                                })
                            }
                        }}
                    >
                        {translate('save')}
                    </Button>
                </Modal>
            ) : (
                <Modal open={state.isWebAppEdit} onClose={onCloseEditingWidget}>
                    <PreviewWebApp
                        ref={previewWebApp}
                        onClose={onCloseEditingWidget}
                        item={state.selectedContent}
                        isEditable={true}
                        indents={'small'}
                    />

                    <Button
                        className={cn(styles.button)}
                        onClick={() => {
                            if (previewWebApp.current && previewWebApp.current.save) {
                                previewWebApp.current.save(() => {
                                    onCloseEditingWidget()
                                })
                            }
                        }}
                    >
                        {translate('save')}
                    </Button>
                </Modal>
            )}
        </React.Fragment>
    )
}

export default EditorFiles
